import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import api from '../../api/axios';
import GbpScoreMatrix from './GbpScoreMatrix';
import {
  Loader2, RefreshCw, Download, AlertTriangle, CalendarDays, Camera, Info,
} from 'lucide-react';

/**
 * GbpReportTab — ofis raporu: günlük/aylık yorum ve puan değişimi
 *
 * Lokasyon listesinden farklı olarak bu sekme CANLI API'ye çıkmaz;
 * `gbp_daily_stats`'taki gecelik fotoğrafları okur. Google "dün kaç yorum
 * geldi" diye bir uç vermediği için fark ancak iki fotoğrafın arasından çıkar.
 *
 * Tarihçe cron'la (23:50 TR) büyür, geriye dönük doldurulmaz: bugün silinmiş
 * yorumlar geçmişte görünmediğinden yeniden kurulan tarihçe Google'ın o gün
 * gösterdiği sayıyı tutmazdı.
 *
 * Üçüncü görünüm "Puan Matrisi" bu kısıtın DIŞINDA: orada hücreler yorumların
 * kendi tarihinden hesaplandığı için geçmiş aylar da dolu gelir (GbpScoreMatrix).
 */

const nfmt = new Intl.NumberFormat('tr-TR');
const num = (v) => (v == null ? '—' : nfmt.format(v));

/** Puan tr-TR'de virgüllü. Lokasyon puanı Google'dan tek haneli gelir; toplam ağırlıklı ortalama olduğu için iki hane taşır. */
const rfmt = (v, digits = 2) =>
  v == null ? '—' : v.toLocaleString('tr-TR', { minimumFractionDigits: digits, maximumFractionDigits: digits });

const dfmt = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
};

const MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

const periodLabel = (p, granularity) => {
  if (granularity !== 'monthly') return dfmt(p);
  const [y, m] = p.split('-');
  return `${MONTHS[Number(m) - 1]} ${y}`;
};

const todayISO = () => {
  const f = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Istanbul', year: 'numeric', month: '2-digit', day: '2-digit',
  });
  return f.format(new Date());
};

const shiftDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

/** Gelen yorum rozeti — artış yeşil, ölçüm yoksa gri çizgi. */
function DeltaBadge({ value }) {
  if (value == null) {
    return <span className="text-gray-300" title="Bu dönemde ölçüm yapılmadı">—</span>;
  }
  if (value === 0) return <span className="text-gray-400">0</span>;
  const up = value > 0;
  return (
    <span className={`font-medium ${up ? 'text-emerald-600' : 'text-rose-600'}`}>
      {up ? '+' : ''}{nfmt.format(value)}
    </span>
  );
}

/** Puan değişimi: Google puanı 0,1 adımlarla oynadığı için çoğu gün sabittir. */
function RatingCell({ prev, cur }) {
  const moved = prev != null && cur != null && Math.abs(cur - prev) > 0.0001;
  return (
    <span className={moved ? (cur > prev ? 'text-emerald-600 font-medium' : 'text-rose-600 font-medium') : 'text-gray-900'}>
      {rfmt(cur, 1)}
      {moved && <span className="ml-1 text-[10px]">{cur > prev ? '▲' : '▼'}</span>}
    </span>
  );
}

export default function GbpReportTab({ brandId, brandName }) {
  // 'table' = günlük/aylık karşılaştırma, 'matrix' = lokasyon × ay puan tablosu
  const [view, setView] = useState('table');
  const [granularity, setGranularity] = useState('daily');
  const [to, setTo] = useState(todayISO());
  const [from, setFrom] = useState(shiftDays(todayISO(), -13));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [snapping, setSnapping] = useState(false);
  // Müşterinin istasyon listesi tanımlıysa (Europcar/Goldcar) marka grubu seçilir
  const [group, setGroup] = useState(null);
  const [groups, setGroups] = useState([]);

  const load = useCallback(async () => {
    if (view === 'matrix') return; // matris kendi ucundan okur
    setLoading(true);
    setErr(null);
    try {
      const r = await api.get('/gbp-brand/report', {
        params: { from, to, granularity, ...(group ? { group } : {}) },
        headers: { 'X-Brand-ID': brandId },
      });
      setData(r.data);
      setGroups(r.data?.groups || []);
      if (!group && r.data?.group) setGroup(r.data.group);
    } catch (e) {
      setErr(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [brandId, from, to, granularity, view, group]);

  useEffect(() => { load(); }, [load]);

  /** Tarihçeyi elle başlatır/tamamlar — cron gün sonunda kendisi koşar. */
  const snapshot = async () => {
    setSnapping(true);
    try {
      await api.post('/gbp-brand/report/snapshot', {}, { headers: { 'X-Brand-ID': brandId } });
      await load();
    } catch (e) {
      setErr(e?.response?.data?.error || e.message);
    } finally {
      setSnapping(false);
    }
  };

  // Aylık kırılıma geçince 14 günlük pencere anlamsız — aralığı da büyüt
  const setMode = (g) => {
    if (g === 'matrix') { setView('matrix'); return; }
    setView('table');
    setGranularity(g);
    const t = todayISO();
    setTo(t);
    setFrom(g === 'monthly' ? shiftDays(t, -364) : shiftDays(t, -13));
  };

  const activeMode = view === 'matrix' ? 'matrix' : granularity;
  const modeToggle = (
    <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
      {[['daily', 'Günlük'], ['monthly', 'Aylık'], ['matrix', 'Puan Matrisi']].map(([v, label]) => (
        <button
          key={v}
          onClick={() => setMode(v)}
          className={`px-3 py-2 text-sm ${activeMode === v ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
        >
          {label}
        </button>
      ))}
    </div>
  );

  const groupToggle = groups.length > 1 ? (
    <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
      {groups.map((g) => (
        <button
          key={g.key}
          onClick={() => setGroup(g.key)}
          className={`px-3 py-2 text-sm ${group === g.key ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
        >
          {g.label}
        </button>
      ))}
    </div>
  ) : null;
  const groupLabel = groups.find((g) => g.key === group)?.label;
  const fileName = groupLabel || brandName;

  const periods = data?.periods || [];

  /** Excel: müşterinin istediği tek düz tablo — her satır bir tarih × lokasyon, dönem sonunda TOPLAM. */
  const rowsForExport = useMemo(() => {
    const out = [];
    for (const p of periods) {
      for (const l of p.locations) {
        out.push({
          'Tarih': periodLabel(p.period, granularity),
          'Lokasyon': l.title,
          'Önceki Yorum': l.prevCount ?? '',
          'Bugünkü Yorum': l.count ?? '',
          'Gelen Yorum': l.newReviews ?? '',
          'Önceki Puan': l.prevRating ?? '',
          'Bugünkü Puan': l.rating ?? '',
          'Ölçüm Tarihi': dfmt(l.measuredAt),
          'Not': l.stale ? 'Bu dönemde ölçüm yapılamadı — son bilinen değer' : '',
        });
      }
      out.push({
        'Tarih': periodLabel(p.period, granularity),
        'Lokasyon': 'TOPLAM',
        'Önceki Yorum': p.total.prevCount ?? '',
        'Bugünkü Yorum': p.total.count ?? '',
        'Gelen Yorum': p.total.newReviews ?? '',
        'Önceki Puan': p.total.prevRating != null ? Number(p.total.prevRating.toFixed(2)) : '',
        'Bugünkü Puan': p.total.rating != null ? Number(p.total.rating.toFixed(2)) : '',
        'Ölçüm Tarihi': '',
        'Not': p.total.staleLocations
          ? `${p.total.staleLocations} lokasyon ölçülemedi — gelen yorum toplamı eksik olabilir`
          : '',
      });
    }
    return out;
  }, [periods, granularity]);

  const exportExcel = () => {
    if (!rowsForExport.length) return;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rowsForExport), 'Rapor');
    const slug = (fileName || 'marka').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    XLSX.writeFile(wb, `gbp-${granularity === 'monthly' ? 'aylik' : 'gunluk'}-rapor-${slug}-${from}_${to}.xlsx`);
  };

  const history = data?.history;
  const noHistory = !loading && !err && !periods.length;

  if (view === 'matrix') {
    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">{modeToggle}{groupToggle}</div>
        <GbpScoreMatrix
          brandId={brandId}
          brandName={fileName}
          group={group}
          onGroups={(gs, g) => { setGroups(gs); if (!group && g) setGroup(g); }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Kontroller */}
      <div className="flex flex-wrap items-end gap-3">
        {modeToggle}
        {groupToggle}
        <label className="text-xs text-gray-500">
          Başlangıç
          <input
            type="date" value={from} max={to}
            onChange={(e) => setFrom(e.target.value)}
            className="block mt-1 px-2 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-900"
          />
        </label>
        <label className="text-xs text-gray-500">
          Bitiş
          <input
            type="date" value={to} min={from} max={todayISO()}
            onChange={(e) => setTo(e.target.value)}
            className="block mt-1 px-2 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-900"
          />
        </label>
        <button
          onClick={load} disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Yenile
        </button>
        <button
          onClick={exportExcel} disabled={!rowsForExport.length}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm hover:bg-emerald-700 disabled:opacity-40"
        >
          <Download className="w-4 h-4" /> Excel indir
        </button>
        <button
          onClick={snapshot} disabled={snapping}
          title="Bugünün puan/yorum fotoğrafını şimdi çeker. Cron her gece 23:50'de kendisi koşar."
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
        >
          {snapping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
          Bugünü ölç
        </button>
      </div>

      {history?.days > 0 && (
        <p className="text-xs text-gray-500 flex items-center gap-1.5">
          <CalendarDays className="w-3.5 h-3.5" />
          Tarihçe {dfmt(history.firstDate)} — {dfmt(history.lastDate)} arasında {nfmt.format(history.days)} gün.
          Her gece 23:50'de bir fotoğraf eklenir.
        </p>
      )}

      {err && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {err}
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-16 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Rapor hazırlanıyor…
        </div>
      )}

      {noHistory && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 flex items-start gap-2">
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">Bu aralıkta henüz fotoğraf yok.</div>
            <p className="mt-1 text-amber-700">
              Rapor iki günün karşılaştırmasından üretilir; ilk karşılaştırma ikinci
              fotoğraftan sonra çıkar. "Bugünü ölç" ile tarihçeyi hemen başlatabilirsiniz.
            </p>
          </div>
        </div>
      )}

      {/* Dönem dönem tablolar */}
      {!loading && periods.map((p) => (
        <div key={p.period} className="rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
            <div className="font-medium text-gray-900 text-sm">{periodLabel(p.period, granularity)}</div>
            <div className="text-xs text-gray-500">
              {nfmt.format(p.total.locations)} lokasyon
              {p.total.staleLocations > 0 && (
                <span className="ml-2 text-amber-700">
                  · {p.total.staleLocations} lokasyon ölçülemedi
                </span>
              )}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wide text-gray-500 bg-white border-b border-gray-100">
                  <th className="text-left font-medium px-4 py-2">Lokasyon</th>
                  <th className="text-right font-medium px-3 py-2">Önceki Yorum</th>
                  <th className="text-right font-medium px-3 py-2">Bugünkü Yorum</th>
                  <th className="text-right font-medium px-3 py-2">Gelen Yorum</th>
                  <th className="text-right font-medium px-3 py-2">Önceki Puan</th>
                  <th className="text-right font-medium px-3 py-2">Bugünkü Puan</th>
                </tr>
              </thead>
              <tbody>
                {p.locations.map((l) => (
                  <tr key={l.locationId} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/60">
                    <td className="px-4 py-2 text-gray-900">
                      {l.title || l.locationId}
                      {l.stale && (
                        <span
                          className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200"
                          title={`Bu dönemde ölçüm yapılamadı. Gösterilen değer ${dfmt(l.measuredAt)} tarihli.`}
                        >
                          ölçülemedi
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-500 tabular-nums">{num(l.prevCount)}</td>
                    <td className="px-3 py-2 text-right text-gray-900 tabular-nums">{num(l.count)}</td>
                    <td className="px-3 py-2 text-right tabular-nums"><DeltaBadge value={l.newReviews} /></td>
                    <td className="px-3 py-2 text-right text-gray-500 tabular-nums">{rfmt(l.prevRating, 1)}</td>
                    <td className="px-3 py-2 text-right tabular-nums"><RatingCell prev={l.prevRating} cur={l.rating} /></td>
                  </tr>
                ))}
                <tr className="bg-gray-50 font-medium border-t border-gray-200">
                  <td className="px-4 py-2 text-gray-900">TOPLAM</td>
                  <td className="px-3 py-2 text-right text-gray-600 tabular-nums">{num(p.total.prevCount)}</td>
                  <td className="px-3 py-2 text-right text-gray-900 tabular-nums">{num(p.total.count)}</td>
                  <td className="px-3 py-2 text-right tabular-nums"><DeltaBadge value={p.total.newReviews} /></td>
                  <td className="px-3 py-2 text-right text-gray-600 tabular-nums">{rfmt(p.total.prevRating)}</td>
                  <td className="px-3 py-2 text-right text-gray-900 tabular-nums">{rfmt(p.total.rating)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {!loading && periods.length > 0 && (
        <p className="text-xs text-gray-400">
          TOPLAM satırındaki puan, lokasyon puanlarının düz ortalaması değil yorum
          sayısına göre ağırlıklı ortalamasıdır. Lokasyon puanı Google'dan tek haneli
          gelir (0,1 adımlarla oynar); toplam iki hane taşır.
        </p>
      )}
    </div>
  );
}
